
import React, { useMemo } from "react";
import { Bar, BarChart as RechartsBarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, TooltipProps, CartesianGrid, Legend, LegendProps } from "recharts";

interface BarChartProps {
  data: any[];
  index: string;
  categories: string[];
  colors?: string[];
  valueFormatter?: (value: number) => string;
  startEndOnly?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showLegend?: boolean;
  yAxisWidth?: number;
  className?: string;
  customTooltip?: (props: TooltipProps<any, any>) => JSX.Element;
}

export function BarChart({
  data,
  index,
  categories,
  colors = ["hsl(var(--primary))", "#2563eb", "#16a34a", "#d97706", "#dc2626"],
  valueFormatter = (value: number) => value.toString(),
  startEndOnly = false,
  showXAxis = true,
  showYAxis = true,
  showLegend = true,
  yAxisWidth = 56,
  className,
  customTooltip,
}: BarChartProps) {
  // Format the data for display
  const formattedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
    }));
  }, [data]);

  // Custom tooltip component if not provided
  const defaultTooltip = useMemo(() => {
    return (props: TooltipProps<any, any>) => {
      const { active, payload } = props;

      if (!active || !payload) return null;

      return (
        <div className="bg-background border rounded-md shadow-md p-2 text-sm">
          <div className="font-medium mb-1">{payload[0]?.payload[index]}</div>
          {payload.map((entry, i) => (
            <div key={i} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">
                {entry.name}: {valueFormatter(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    };
  }, [index, valueFormatter]);

  // Custom legend
  const renderLegend = (props: LegendProps) => {
    const { payload } = props;

    return (
      <div className="flex justify-center gap-6 text-sm">
        {payload?.map((entry, index) => (
          <div key={`legend-${index}`} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };
  
  // Format tick values for the x-axis
  const formatXAxisTick = (value: any, index: number) => {
    if (startEndOnly) {
      // Only show first and last labels
      if (index === 0 || index === data.length - 1) {
        return value;
      }
      return "";
    }
    return value;
  };

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart
          data={formattedData}
          margin={{ top: 10, right: 10, left: 0, bottom: 30 }}
        >
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.3} />
          
          {showXAxis && (
            <XAxis
              dataKey={index}
              tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={{ stroke: "hsl(var(--border))" }}
              tickFormatter={formatXAxisTick}
              tickMargin={10}
            />
          )}
          
          {showYAxis && (
            <YAxis
              width={yAxisWidth}
              tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => valueFormatter(value)}
            />
          )}
          
          <Tooltip content={customTooltip || defaultTooltip} cursor={{ fill: "hsl(var(--muted))", opacity: 0.1 }} />
          
          {showLegend && <Legend content={renderLegend} />}
          
          {categories.map((category, i) => (
            <Bar
              key={category}
              dataKey={category}
              name={category}
              fill={colors[i % colors.length]}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}
